// src/pages/markets/board/board/useSortFreeze.ts
//
// A selector over the query data, not a cache freeze: values keep updating in
// place, only the rendered order is held. Without this the list re-sorts under
// the cursor every minute and whatever the reader was looking at moves.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ScreenerEvent } from '../api/boardTypes';

export interface SortFreezeResult {
    events: ScreenerEvent[];
    pendingCount: number;
    apply: () => void;
}

export function useSortFreeze(events: ScreenerEvent[], frozen: boolean): SortFreezeResult {
    const [order, setOrder] = useState<string[] | null>(null);
    const eventsRef = useRef(events);
    eventsRef.current = events;

    useEffect(() => {
        if (!frozen) setOrder(null);
    }, [frozen]);

    useEffect(() => {
        if (frozen && order === null) {
            setOrder(eventsRef.current.map(e => e.event_id));
        }
    }, [frozen, order]);

    const apply = useCallback(() => {
        setOrder(eventsRef.current.map(e => e.event_id));
    }, []);

    if (!frozen || order === null) {
        return { events, pendingCount: 0, apply };
    }

    const byId = new Map(events.map(e => [e.event_id, e]));
    const held: ScreenerEvent[] = [];
    for (const id of order) {
        const e = byId.get(id);
        if (e) held.push(e);
    }

    const known = new Set(order);
    // Appended rather than dropped: a new event must still be reachable by
    // scrolling, it just does not get to jump into the visible range.
    const arrived = events.filter(e => !known.has(e.event_id));

    return {
        events: [...held, ...arrived],
        pendingCount: arrived.length,
        apply,
    };
}